import React from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import dayjs, { Dayjs } from 'dayjs';

interface ServiceRoleData {
  validFrom?: string | Date;
  validTo?: string | Date;
  justification?: string;
}

interface ServiceRoleDefinitionData {
  autoDeactivationEnabled?: boolean;
}

interface InputsData {
  validFrom?: string | Date;
  validTo?: string | Date;
}

type Props = {
  serviceRoleUid?: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  handleChangeValidFrom?: (value: Dayjs | null, field: string) => void;
  handleChangeValidTo?: (value: Dayjs | null, field: string) => void;
  serviceRole?: ServiceRoleData;
  inputs?: InputsData;
  error: boolean;
  serviceRoleDefinition?: ServiceRoleDefinitionData;
};

const styles = {
  input: {
    width: '100%',
    '& .MuiInputBase-root': {
      border: 'none',
      height: '3em',
      ml: 0.7,
      padding: 0,
      display: 'flex',
    },
    '& .MuiInputBase-input': {
      border: 'none',
      padding: '8px',
    },
    '& .MuiOutlinedInput-input': {
      border: 'none',
      margin: '0.2em',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  date: {
    height: '3em',
    padding: 0,
    ml: 0.9,
    '& .MuiInputBase-input': {
      cursor: 'pointer',
      padding: '10px',
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      border: 'none',
      height: '3em',
      padding: 0,
    },
    '& .MuiInputBase-inputAdornedEnd': {
      border: 'none',
      height: '3em',
      padding: 0,
      pl: '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiIconButton-edgeEnd': {
      m: 0,
    },
  },
};

export const ServiceRole = ({
  handleChange,
  disabled,
  error,
  inputs,
  handleChangeValidFrom,
  serviceRole,
  handleChangeValidTo,
  serviceRoleDefinition,
}: Props) => {
  return (
    <Grid container sx={{ display: 'flex', backgroundColor: 'white', padding: '1em', mt: -2 }}>
      <Grid item xs={12} sx={{ mb: 4, display: 'flex' }} data-testid={'service-role'}>
        <Grid
          item
          xs={6}
          sx={{
            height: '2em',
            display: 'flex',
            flexDirection: 'column',
            mr: 1,
          }}
          data-testid={'service-role-valid-from'}
        >
          <Typography color="primary" variant={'body2'} sx={{ mb: 1, color: error && !disabled ? 'red' : 'black' }}>
            {`Valid from ${error ? '*' : ''}`}
          </Typography>
          <MobileDatePicker
            sx={styles.date}
            closeOnSelect
            format="DD.MM.YYYY"
            slotProps={{
              actionBar: {
                actions: [],
              },
            }}
            disablePast
            value={inputs?.validFrom ? dayjs(new Date(inputs.validFrom)) : null}
            onChange={(e) => {
              if (handleChangeValidFrom && e) handleChangeValidFrom(e, 'validFrom');
            }}
            disabled={disabled}
          />
        </Grid>

        {serviceRoleDefinition?.autoDeactivationEnabled && (
          <Grid
            item
            xs={6}
            sx={{ height: '2em', display: 'flex', flexDirection: 'column' }}
            data-testid="service-role-valid-to"
          >
            <Typography color="primary" variant={'body2'} sx={{ mb: 1 }}>
              Valid to
            </Typography>
            <MobileDatePicker
              sx={styles.date}
              format="DD.MM.YYYY"
              closeOnSelect
              slotProps={{
                actionBar: {
                  actions: [],
                },
              }}
              disablePast
              shouldDisableDate={(day) =>
                typeof day !== 'string' && inputs?.validFrom
                  ? new Date(day.toISOString()) <= new Date(inputs.validFrom)
                  : false
              }
              disabled={!inputs?.validFrom || disabled}
              value={inputs?.validTo ? dayjs(new Date(inputs.validTo)) : null}
              onChange={(e) => handleChangeValidTo && e && handleChangeValidTo(e, 'validTo')}
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
        <Typography color="primary" variant={'body2'}>
          Justification
        </Typography>
        <TextField
          data-testid="justification"
          sx={styles.input}
          type="text"
          placeholder="Role justification"
          onChange={handleChange}
          name="justification"
          disabled={disabled}
          defaultValue={serviceRole?.justification}
        />
      </Grid>
    </Grid>
  );
};
